import { Divider, Empty, Row, Typography } from "antd";
import React, { useEffect, useMemo } from "react";
import { FieldPathValue, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useGetOrganization } from "../../../../../api/v1";
import { managementService } from "../../../../../configs/constants";
import { useMasterUserOptions } from "../../../../../hooks/useMasterUserOptions";
import { TemplateField } from "../../../../../types/template";
import { SectionLoader } from "../../../../atoms/SectionLoader/SectionLoader";
import { getValidationProps } from "../../../../libs/form";
import { isSelectOption } from "../../../../libs/utils";
import { DynamicFields } from "../../../../organism/DynamicFields";
import { PointOfInterestField } from "../../../../organism/DynamicFields/PointOfInterestField";
import { buildOrgMarketingSelectFields } from "./buildOrgMarketingSelectFields";
import { MarketingMediaUploadField } from "./MarketingMediaUploadField";
import { stripMarketingUrlSchemeForDisplay } from "../Marketing/marketingUrlUtils";

export type MarketingAttributesEntity = "property" | "unit";

type MarketingAttributesProps<T extends FieldValues> = {
  control: UseFormReturn<T>["control"];
  errors: UseFormReturn<T>["formState"]["errors"];
  setValue: UseFormReturn<T>["setValue"];
  entity: MarketingAttributesEntity;
  noEdit?: boolean;
  savedValues?: Record<string, unknown>;
  templateFields?: TemplateField[];
  marketingPublicListUrl?: string;
  /** Optional storage path segments: org/property[/unit]. */
  uploadPathSegments?: {
    propertyName?: string;
    unitCode?: string;
  };
  /** When true (e.g. Marketing tab active), refetch org marketing config. */
  isActive?: boolean;
};

const SECTION_TITLES: Record<string, string> = {
  details: "Property Details",
  media: "Media & Links",
};

const UNIT_SECTION_TITLES: Record<string, string> = {
  details: "Unit Details",
  media: "Media & Links",
};

const ORG_DRIVEN_FIELD_NAMES = new Set(["amenities", "pointsOfInterest"]);

const normalizeFieldRules = (rules: TemplateField["rules"]) => {
  if (!rules || typeof rules !== "object") return rules;

  const nextRules = { ...rules } as Record<string, unknown>;
  const pattern = nextRules.pattern as { value?: string | RegExp; message?: string } | undefined;

  if (pattern && typeof pattern.value === "string") {
    nextRules.pattern = {
      ...pattern,
      value: new RegExp(pattern.value),
    };
  }

  return nextRules as TemplateField["rules"];
};

const isPointOfInterestSelect = (field: TemplateField) =>
  field.name === "pointsOfInterest" && field.type === "select";

const transformAttributeDefaultValue = (field: TemplateField, value: unknown) => {
  if (field.type !== "select") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((option) => transformAttributeDefaultValue({ ...field }, option));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const selectValue = value as Record<string, unknown>;
  if ("key" in selectValue && "value" in selectValue) {
    return {
      value: selectValue.key,
      label: String(selectValue.value ?? ""),
    };
  }

  return value;
};

const isMarketingMediaField = (field: TemplateField) => field.type === "marketingMedia";

const renderTemplateFields = <T extends FieldValues>({
  fields,
  control,
  errors,
  setValue,
  uploadPathSegments,
}: {
  fields: TemplateField[];
  control: UseFormReturn<T>["control"];
  errors: UseFormReturn<T>["formState"]["errors"];
  setValue: UseFormReturn<T>["setValue"];
  uploadPathSegments?: { propertyName?: string; unitCode?: string };
}) =>
  fields.map((field) => {
    if (isMarketingMediaField(field)) {
      const fieldName = `dynamicFields.${field.name}`;
      return (
        <MarketingMediaUploadField
          key={fieldName}
          fieldName={fieldName}
          label={field.label}
          mediaKind={field.mediaKind === "video" ? "video" : "image"}
          control={control}
          colSpan={field.colSpan ?? 12}
          disabled={Boolean(field.isDisabled)}
          helperText={field.helperText}
          uploadPathSegments={uploadPathSegments}
        />
      );
    }

    if (isPointOfInterestSelect(field)) {
      const fieldName = `dynamicFields.${field.name}`;
      const isDisabled = Boolean(field.isDisabled);
      const isRequired = Boolean(field.rules && "required" in field.rules && field.rules.required);
      const pointOfInterestOptions =
        field.options
          ?.filter(isSelectOption)
          .map((option) => ({ label: String(option.label), value: String(option.value) })) || [];

      return (
        <PointOfInterestField
          key={fieldName}
          fieldName={fieldName}
          label={field.label}
          control={control}
          setValue={setValue}
          options={pointOfInterestOptions}
          disabled={isDisabled}
          validationProps={getValidationProps({ fieldName, errors, isRequired })}
        />
      );
    }

    return (
      <DynamicFields
        key={`dynamicFields.${field.name}`}
        template={[field]}
        control={control}
        errors={errors}
        setValue={setValue}
        colSpan={field.colSpan ?? 8}
      />
    );
  });

export const MarketingAttributes = <T extends FieldValues>(props: MarketingAttributesProps<T>) => {
  const {
    control,
    errors,
    setValue,
    entity,
    noEdit = true,
    savedValues,
    templateFields,
    marketingPublicListUrl,
    uploadPathSegments,
    isActive = true,
  } = props;

  const organizationParams = useMemo(
    () => ({
      filterkeys: `marketingDefaultOrgConfig.${String(entity).toLowerCase()}`,
    }),
    [entity],
  );

  const { organization, isLoadingOrganization, refetchOrganization } = useGetOrganization({
    params: organizationParams,
  });

  const { options: masterUserOptions, loading: isLoadingMasterUsers } = useMasterUserOptions(managementService);

  useEffect(() => {
    if (!isActive) return;
    refetchOrganization();
  }, [isActive, entity, refetchOrganization]);

  const orgSelectFields = useMemo(
    () => buildOrgMarketingSelectFields(organization?.marketingDefaultOrgConfig?.[entity]),
    [organization?.marketingDefaultOrgConfig, entity],
  );

  const baseTemplate = useMemo(() => {
    const baseFields = (templateFields ?? []).filter((field) => !ORG_DRIVEN_FIELD_NAMES.has(field.name));
    const fields = [...baseFields, ...orgSelectFields];
    const hasSavedValues = savedValues && Object.keys(savedValues).length > 0;

    return fields
      .map((field) => {
        const rawSavedValue = hasSavedValues ? savedValues?.[field.name] : undefined;
        const savedValue =
          rawSavedValue === undefined ? undefined : transformAttributeDefaultValue(field, rawSavedValue);
        const hasMeaningfulSavedValue =
          savedValue !== undefined &&
          savedValue !== null &&
          !(typeof savedValue === "string" && savedValue.trim() === "");
        const qrCodeDefault =
          field.name === "qrCodeUrl"
            ? stripMarketingUrlSchemeForDisplay(field.default || marketingPublicListUrl || "")
            : field.default;

        let nextDefault = hasMeaningfulSavedValue ? savedValue : qrCodeDefault;

        // No saved amenities (e.g. detail has no marketingData) → select all org options.
        if (
          field.name === "amenities" &&
          !(savedValues && Object.prototype.hasOwnProperty.call(savedValues, "amenities")) &&
          Array.isArray(field.options)
        ) {
          nextDefault = field.options.filter(isSelectOption).map((option) => ({
            label: String(option.label),
            value: option.value,
          }));
        }

        const nextField: TemplateField = {
          ...field,
          rules: normalizeFieldRules(field.rules),
          default: nextDefault,
          // Keep template isDisabled as field-level lock only (e.g. slug). Edit mode applied at render.
          isDisabled: Boolean(field.isDisabled),
        };

        if (field.name === "marketingAgent") {
          nextField.options = masterUserOptions;
          nextField.rules = {
            ...(normalizeFieldRules(field.rules) || {}),
            required: "Marketing agent is required",
          };
          if (!hasMeaningfulSavedValue) {
            nextField.default = null;
          }
        }

        return nextField;
      })
      .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
  }, [
    orgSelectFields,
    savedValues,
    templateFields,
    marketingPublicListUrl,
    masterUserOptions,
  ]);

  const template = useMemo(
    () =>
      baseTemplate.map((field) => ({
        ...field,
        isDisabled: noEdit || Boolean(field.isDisabled),
      })),
    [baseTemplate, noEdit],
  );

  const sectionTitles = entity === "unit" ? UNIT_SECTION_TITLES : SECTION_TITLES;

  const sectionedFields = useMemo(() => {
    const hasSections = template.some((field) => field.section);
    if (!hasSections) {
      return [{ key: "default", title: "", fields: template }];
    }

    const groups: { key: string; title: string; fields: TemplateField[] }[] = [];
    const sectionOrder: string[] = [];

    template.forEach((field) => {
      const sectionKey = field.section || "other";
      if (!sectionOrder.includes(sectionKey)) {
        sectionOrder.push(sectionKey);
        groups.push({
          key: sectionKey,
          title: sectionTitles[sectionKey] || sectionKey,
          fields: [],
        });
      }

      groups.find((group) => group.key === sectionKey)?.fields.push(field);
    });

    return groups;
  }, [template, sectionTitles]);

  useEffect(() => {
    baseTemplate.forEach((field) => {
      if (field.type === "marketingMedia" && field.default !== undefined) {
        const fieldName = `dynamicFields.${field.name}` as Path<T>;
        setValue(fieldName, field.default as FieldPathValue<T, typeof fieldName>);
        return;
      }

      if (!isPointOfInterestSelect(field) || field.default === undefined) return;

      const fieldName = `dynamicFields.${field.name}` as Path<T>;
      setValue(fieldName, field.default as FieldPathValue<T, typeof fieldName>);

      const distancesKey = `${field.name}Distances`;
      const savedDistances = savedValues?.[distancesKey];
      if (savedDistances && typeof savedDistances === "object") {
        const distancesFieldName = `dynamicFields.${distancesKey}` as Path<T>;
        setValue(distancesFieldName, savedDistances as FieldPathValue<T, typeof distancesFieldName>);
      }
    });
  }, [baseTemplate, savedValues, setValue]);

  return (
    <SectionLoader isLoading={isLoadingOrganization || isLoadingMasterUsers} containerStyle={{ minHeight: "8rem" }}>
      {template.length === 0 ? (
        <Empty description="No marketing attributes available" style={{ margin: "2rem 0" }} />
      ) : (
        sectionedFields.map((section, index) => (
          <div key={section.key}>
            {section.title ? (
              <>
                {index > 0 ? <Divider style={{ margin: "8px 0 16px" }} /> : null}
                <Typography.Title level={5} className="ag-primary" style={{ marginBottom: 16 }}>
                  {section.title}
                </Typography.Title>
              </>
            ) : null}
            {section.key === "media" && section.fields.some(isMarketingMediaField) ? (
              <>
                <Row gutter={[16, 8]}>
                  {renderTemplateFields({
                    fields: section.fields.filter((field) => !isMarketingMediaField(field)),
                    control,
                    errors,
                    setValue,
                    uploadPathSegments,
                  })}
                </Row>
                <Divider style={{ margin: "16px 0" }} />
                <Row gutter={[16, 8]}>
                  {renderTemplateFields({
                    fields: section.fields.filter(isMarketingMediaField),
                    control,
                    errors,
                    setValue,
                    uploadPathSegments,
                  })}
                </Row>
              </>
            ) : (
              <Row gutter={[16, 8]}>
                {renderTemplateFields({
                  fields: section.fields,
                  control,
                  errors,
                  setValue,
                  uploadPathSegments,
                })}
              </Row>
            )}
          </div>
        ))
      )}
    </SectionLoader>
  );
};

export default MarketingAttributes;
